import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import { PopupContext } from '@app/dev/contexts/popup';
import { useTranslation } from '@common/hooks/translation';
import { usePrevious } from '@common/hooks/previous';
import { getPlayerInfo, HandleStartFinish } from '@app/dev/helpers/clientTransaction';
import { broadcast, broadcastChannels } from '@common/helpers/broadcast';
import { UserInactivity } from './steps/userInactivity';
import { getStepsList } from './stepsConfig';
import { backStepNames, nextStepNames, normalizeAmount } from './helper';
import './style.scss';
import { POPUPS } from '@common/constants/popups';

const ClientDepositPopup = () => {
  const t = useTranslation();
  const [{ closePopup, closePopupByType }] = useContext(PopupContext);

  const [form, setForm] = useState({
    searchType: 'playerId',
    amount: '',
    playerId: null,
    playerName: '',
    maxDepositAmount: null,
    playerInfo: null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isBackDisabled, setBackDisabled] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const [timerResetTrigger, setTimerResetTrigger] = useState(0);

  const finishCollectDepositRef = useRef(null);
  const prevAmount = usePrevious(form.amount);

  const steps = useMemo(() => getStepsList(), []);
  const currentStepName = steps[currentStep].name;
  const stepData = steps[currentStep].step;
  const StepComponent = stepData.component;
  const isSuccessStep = currentStepName === 'deposit_success';
  const isLastStep = currentStep === steps.length - 1;
  const shouldHideBackButton = currentStepName === 'deposit_amount';
  const successStepIndex = useMemo(
    () => steps.findIndex(({ name }) => name === 'deposit_success'),
    [steps],
  );

  const triggerFinishCollect = useCallback(() => {
    if (finishCollectDepositRef.current) {
      finishCollectDepositRef.current();
    }

    HandleStartFinish.finish();
    finishCollectDepositRef.current = null;
  }, []);

  const handleCloseWithFinish = useCallback(() => {
    triggerFinishCollect();
    closePopupByType(POPUPS.CLIENT_DEPOSIT_POPUP);
  }, [triggerFinishCollect, closePopupByType]);

  const showSuccessStep = useCallback(() => {
    setIsInactive(false);
    setBackDisabled(false);

    if (successStepIndex >= 0) {
      setCurrentStep(successStepIndex);
    } else {
      handleCloseWithFinish();
    }
  }, [successStepIndex, handleCloseWithFinish]);

  const onContinue = async () => {
    const formErrors = stepData.validate(form);

    setErrors(formErrors);

    if (!_.isEmpty(formErrors)) return;

    if (currentStep === 0) {
      setLoading(true);

      let result;

      try {
        const searchData = form.searchType === 'playerId'
          ? { id: form.playerId }
          : { login: form.playerName };

        // Keep usage simple: await getPlayerInfo and act on playerInfo
        result = await getPlayerInfo(searchData);
      } catch (error) {
        setErrors({
          ...formErrors,
          submit: 'Failed to load player info. Please try again.',
        });
      } finally {
        setLoading(false);
      }
      if (!result?.playerInfo || result?.playerInfo?.Error) {
        const isPlayerIdSearch = form.searchType === 'playerId';
        const errorKey = isPlayerIdSearch ? 'playerId' : 'playerName';
        const notFoundMessage = isPlayerIdSearch
          ? 'Player with this ID is not found'
          : 'Player with this username is not found';

        setErrors({
          ...formErrors,
          [errorKey]: notFoundMessage,
        });

        return;
      }

      const { FirstName, LastName, Id } = result.playerInfo;
      const playerFullName = [FirstName, LastName].filter(Boolean).join(' ').trim();

      setForm((prevForm) => ({
        ...prevForm,
        playerId: Id ?? prevForm.playerId,
        playerName: playerFullName || prevForm.playerName,
        maxDepositAmount: Number.isFinite(result.maxDepositAmount) ? result.maxDepositAmount : null,
        playerInfo: result.playerInfo,
      }));

      // If maxDepositAmount wasn't delivered yet, listen once and update when it arrives
      if (!Number.isFinite(result.maxDepositAmount)) {
        const onMaxDeposit = (e) => {
          const numeric = Number(e.detail);
          if (Number.isFinite(numeric)) {
            setForm((prevForm) => ({
              ...prevForm,
              maxDepositAmount: numeric,
            }));
          }
        };

        broadcast.addEventListener(
          broadcastChannels.ReceivePlayerMaxDepositAmount,
          onMaxDeposit,
          { once: true },
        );
      }
    }

    if (!isLastStep) {
      if (currentStepName === 'deposit_amount') {
        const numericAmount = normalizeAmount(form.amount);

        if (numericAmount <= 0) {
          setErrors((prev) => ({
            ...prev,
            submit: t('Please insert deposit amount before continuing.'),
          }));
          return;
        }

        setLoading(true);

        setTimeout(() => {
          setLoading(false);
          triggerFinishCollect();
          showSuccessStep();
        }, 1000);

        return;
      }

      setCurrentStep(currentStep + 1);
      return;
    }

    if (currentStepName === 'deposit_success') {
      handleCloseWithFinish();
    }
  };

  useEffect(() => () => handleCloseWithFinish(), [handleCloseWithFinish]);

  useEffect(() => {
    if (currentStepName !== 'deposit_amount') {
      setBackDisabled(false);
      setIsInactive(false);
    }
  }, [currentStepName]);

  const handleAmountCollected = useCallback(() => setBackDisabled(true), []);

  const handleInactivity = useCallback(() => {
    if (!isInactive) setIsInactive(true);
  }, [isInactive]);

  const handleResumeDeposit = useCallback(() => {
    setIsInactive(false);
    setTimerResetTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const currentAmount = normalizeAmount(form.amount);
    const lastAmount = normalizeAmount(prevAmount || '');
    if (isInactive && currentAmount > lastAmount && currentAmount > 0) {
      handleResumeDeposit();
    }
  }, [form.amount, prevAmount, isInactive, handleResumeDeposit]);

  const registerFinishCollectHandler = useCallback((handler) => {
    finishCollectDepositRef.current = handler || null;
  }, []);

  useEffect(() => {
    if (currentStepName === 'deposit_amount') {
      const finish = HandleStartFinish.start((amount) => {
        const numericAmount = normalizeAmount(amount);
        setForm((prevForm) => ({
          ...prevForm,
          amount: numericAmount,
        }));

        if (numericAmount > 0) {
          handleAmountCollected();
        }
      });

      const finishWrapper = () => {
        finish?.();
      };

      registerFinishCollectHandler(finishWrapper);

      return () => {
        finish?.();
        registerFinishCollectHandler(null);
      };
    }

    return undefined;
  }, [currentStepName, handleAmountCollected, registerFinishCollectHandler]);

  const handleDepositFromInactivity = useCallback(() => {
    const numericAmount = normalizeAmount(form.amount);

    if (numericAmount <= 0) {
      triggerFinishCollect();
      setIsInactive(false);
      closePopup();
      return;
    }

    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      triggerFinishCollect();
      showSuccessStep();
    }, 500);
  }, [form.amount, showSuccessStep, triggerFinishCollect, closePopup]);

  const onBack = useCallback(() => {
    if (currentStep === 0) {
      handleCloseWithFinish();
      return;
    }

    setCurrentStep(currentStep - 1);
    setErrors({});

    if (currentStep - 1 === 1) {
      setForm((prevForm) => ({ ...prevForm, amount: '' }));
    }

    if (currentStep === 1) {
      setForm((prevForm) => ({
        ...prevForm,
        playerId: null,
        playerName: '',
        playerInfo: null,
        maxDepositAmount: null,
        amount: '',
      }));
    }
  }, [currentStep, handleCloseWithFinish]);

  const baseStepProps = useMemo(() => ({
    form,
    setForm,
    errors,
    setErrors,
  }), [form, errors]);

  const stepProps = useMemo(() => {
    const props = { ...baseStepProps };

    if (currentStepName === 'deposit_amount') {
      return {
        form,
        onInactivity: handleInactivity,
        timerResetTrigger,
        onClose: handleCloseWithFinish,
      };
    }

    if (['search_player', 'client_deposit_player_confirmation'].includes(currentStepName)) {
      return { ...props, onClose: handleCloseWithFinish };
    }

    if (isSuccessStep) {
      return { form, onClose: handleCloseWithFinish };
    }

    return props;
  }, [
    baseStepProps,
    form,
    currentStepName,
    handleInactivity,
    timerResetTrigger,
    isSuccessStep,
    handleCloseWithFinish,
  ]);

  return (
    <div className={`popup-content popup-small client-deposit-popup popup-${currentStepName}`}>
      {isInactive ? (
        <UserInactivity
          amount={form.amount}
          loading={loading}
          onContinue={handleResumeDeposit}
          onDeposit={handleDepositFromInactivity}
          onTimeout={handleDepositFromInactivity}
          onClose={handleCloseWithFinish}
        />
      ) : (
        <StepComponent {...stepProps} />
      )}
      {!isInactive && !isSuccessStep && (
        <div className="popup-actions">
          {!shouldHideBackButton && (
            <button
              onClick={onBack}
              className="popup-cancel-button"
              type="button"
              disabled={loading || (isBackDisabled && currentStepName === 'deposit_amount')}
            >
              {t(backStepNames[currentStep])}
            </button>
          )}

          <button
            type="button"
            onClick={onContinue}
            className={classnames('popup-confirm-button', { disabled: loading })}
            disabled={loading}
          >
            {t(nextStepNames[currentStep])}
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientDepositPopup;
